/**
 * The one signal that the add-in is replacing the whole document body, as
 * `replaceDocumentFromBase64` does when a chat snapshot is restored. Word
 * gives every paragraph of the restored file a new `uniqueLocalId`, so each
 * part of the anchor layer that remembers ids (the change ledger, a live
 * proposal's followed paragraphs) drops them at "begin" and reads the
 * document afresh at "end". Bookmarks come back with the restored file, so a
 * tracker finds its redline again by name.
 *
 * `document.ts` announces the two phases around its write. The anchor layer
 * subscribes through `WordDocument.onBodyReplaced`, which `OfficeWordDocument`
 * serves from here and `WordDocumentFixture` serves from its own list, so a
 * replacement can be driven in a test without Word.
 */
export type BodyReplacementPhase = "begin" | "end";

export type BodyReplacementListener = (phase: BodyReplacementPhase) => void;

export class BodyReplacement {
    private static readonly listeners = new Set<BodyReplacementListener>();

    /** Tells every subscriber the body is about to be replaced ("begin") or has just been ("end"). */
    static announce(phase: BodyReplacementPhase): void {
        for (const listener of Array.from(BodyReplacement.listeners)) listener(phase);
    }

    /** Subscribes to both phases; returns the unsubscribe. */
    static subscribe(listener: BodyReplacementListener): () => void {
        BodyReplacement.listeners.add(listener);
        return () => {
            BodyReplacement.listeners.delete(listener);
        };
    }
}
